import { useCallback, useEffect, useState } from "react";

import { ClientInfoState } from "shared/core/state/clientInfoState";
import { changeLanguage } from "i18next";
import { localeCodeIds, LocaleIdToCodeMap } from "shared/core/locale";
import { useUserContext } from "core/state/UserContext/UserContext";
import { ModulePermissionKeys, modulePermissions } from "shared/constants/Modules";

export type ManualLocales = "en" | "en_AU" | "en_NZ";

export abstract class ManualLocaleState {
  public static get(): ManualLocales | undefined {
    return (localStorage.getItem("manualLocale") as ManualLocales) ?? undefined;
  }

  public static set(value: ManualLocales) {
    localStorage.setItem("manualLocale", value);
  }

  public static clear() {
    localStorage.removeItem("manualLocale");
  }
}

export default function useLocale() {
  const { user } = useUserContext();

  const getLocaleId = useCallback(() => {
    const client = ClientInfoState.toJson();
    const manualLocale = ManualLocaleState.get();

    return user?.localeId ?? client?.localeId ?? (manualLocale && localeCodeIds[manualLocale]) ?? "";
  }, [user?.localeId]);

  const [localeId, setLocaleId] = useState<string>(getLocaleId());

  useEffect(() => {
    const localeId = getLocaleId();
    setLocaleId(localeId);
  }, [getLocaleId, user]);

  const updateLocale = useCallback((newLocale: ManualLocales) => {
    ManualLocaleState.set(newLocale);
    setLocaleId(localeCodeIds[newLocale]);
    changeLanguage(newLocale);
  }, []);

  const isLocalePermitted = (module: ModulePermissionKeys) => modulePermissions[module]?.includes(LocaleIdToCodeMap[localeId]);

  return { localeId, updateLocale, isLocalePermitted } as const;
}
